import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  acknowledgeCashin,
  getPendingCashins,
  type CashinOutcomeError,
  type CashinProbeState,
  type PendingCashin,
} from '@shared/services/api/cashinService';

import type { PendingCashinRecord } from './pendingCashinStorage';
import { useCashinVerification } from './useCashinVerification';

export const CASHIN_RESOLVED_HOLD_MS = 10_000;
export const CASHIN_FADE_MS = 400;

export type InterruptedCashinState = CashinProbeState;

export type CashinResolvedStatus = 'completed' | 'rejected';

export type CashinBannerStatus = 'tracking' | CashinResolvedStatus;

export interface InterruptedCashinNotice {
  pending: PendingCashinRecord;
  state: InterruptedCashinState;
  final: boolean;
  transfer?: Record<string, unknown>[] | null;
  error?: CashinOutcomeError;
  createdAt?: string;
}

export interface CashinProgressBanner {
  status: CashinBannerStatus;
  fading: boolean;
}

export interface CashinProgressToaster {
  open: boolean;
  type: 'success' | 'error';
  status: CashinResolvedStatus;
}

const closedToaster: CashinProgressToaster = {
  open: false,
  type: 'success',
  status: 'completed',
};

const noticeKey = (requestId: string, state?: CashinProbeState) => `${requestId}:${state ?? ''}`;

export const isFinalNoticeState = (state?: CashinProbeState): state is CashinResolvedStatus =>
  state === 'completed' || state === 'rejected';

const isAcknowledgedOnClose = (state?: CashinProbeState): boolean =>
  isFinalNoticeState(state) || state === 'expired';

const OPEN_CASHIN_STATES = new Set<InterruptedCashinState>(['in_progress', 'unknown']);

export interface FollowedCashin {
  requestId: string;
  state: InterruptedCashinState;
  transfer: Record<string, unknown>[] | null;
  cellphone: string;
  amount: string;
  accountOwner: string;
  ciSender: string;
}

export const isCashinAwaitingResult = (
  session: { banner: CashinProgressBanner | null; notice: InterruptedCashinNotice | null } | null
): boolean => {
  if (!session) {
    return false;
  }
  if (session.banner?.status === 'tracking') {
    return true;
  }
  return session.notice != null && OPEN_CASHIN_STATES.has(session.notice.state);
};

const startedAtMs = (createdAt?: string): number | null => {
  if (!createdAt) {
    return null;
  }
  const parsed = Date.parse(createdAt);
  return Number.isFinite(parsed) ? parsed : null;
};

const toRecord = (item: PendingCashin): PendingCashinRecord => ({
  request_id: item.request_id,
  cellphone: item.cellphone,
  ci_sender: item.ci_sender,
  amount: item.amount,
  account_owner: item.account_owner ?? '',
});

export const useInterruptedCashin = (watchKey: string, enabled: boolean) => {
  const [items, setItems] = useState<PendingCashin[]>([]);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  const [trackedId, setTrackedId] = useState<string | null>(null);
  const [banner, setBanner] = useState<CashinProgressBanner | null>(null);
  const [toaster, setToaster] = useState<CashinProgressToaster>(closedToaster);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      setTrackedId(null);
      setBanner(null);
      setToaster(closedToaster);
      return undefined;
    }
    let cancelled = false;
    getPendingCashins()
      .then((body) => {
        if (!cancelled) {
          setItems(body?.errors?.length ? [] : (body?.data ?? []));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [watchKey, enabled]);

  const current = useMemo(
    () => items.find((item) => !dismissed.has(noticeKey(item.request_id, item.state))) ?? null,
    [items, dismissed]
  );
  const verifiedId = current && !isAcknowledgedOnClose(current.state) ? current.request_id : null;
  const trackingId = trackedId && trackedId !== verifiedId ? trackedId : null;
  const originFor = (id: string | null) =>
    startedAtMs(items.find((item) => item.request_id === id)?.created_at);
  const verification = useCashinVerification(verifiedId, originFor(verifiedId));
  const tracking = useCashinVerification(trackingId, originFor(trackingId));

  const notice = useMemo<InterruptedCashinNotice | null>(() => {
    if (!current) {
      return null;
    }
    const probe = verification.probe ?? current;
    const state = probe.state ?? 'in_progress';
    return {
      pending: toRecord(current),
      state,
      final: isFinalNoticeState(state),
      transfer: probe.transfer ?? null,
      error: probe.error,
      createdAt: current.created_at,
    };
  }, [current, verification.probe]);

  const trackedState = tracking.final ? tracking.probe?.state : undefined;

  useEffect(() => {
    if (!trackedState) {
      return;
    }
    if (isFinalNoticeState(trackedState)) {
      setBanner({ status: trackedState, fading: false });
      setToaster({ open: true, type: trackedState === 'completed' ? 'success' : 'error', status: trackedState });
      return;
    }
    setTrackedId(null);
    setBanner(null);
  }, [trackedState]);

  const bannerStatus = banner?.status;
  const bannerFading = banner?.fading ?? false;

  useEffect(() => {
    if (!isFinalNoticeState(bannerStatus) || bannerFading) {
      return undefined;
    }
    const holdId = setTimeout(() => {
      setBanner((prev) => (prev ? { ...prev, fading: true } : prev));
    }, CASHIN_RESOLVED_HOLD_MS);
    return () => clearTimeout(holdId);
  }, [bannerStatus, bannerFading]);

  useEffect(() => {
    if (!bannerFading) {
      return undefined;
    }
    const fadeId = setTimeout(() => {
      setTrackedId(null);
      setBanner(null);
      setToaster(closedToaster);
    }, CASHIN_FADE_MS);
    return () => clearTimeout(fadeId);
  }, [bannerFading]);

  const dismiss = useCallback(() => {
    if (!notice) {
      return;
    }
    const requestId = notice.pending.request_id;
    if (notice.final || notice.state === 'expired') {
      acknowledgeCashin(requestId).catch(() => undefined);
      setItems((prev) => prev.filter((item) => item.request_id !== requestId));
      return;
    }
    setDismissed((prev) => new Set(prev).add(noticeKey(requestId, current?.state)));
    setTrackedId(requestId);
    setBanner({ status: 'tracking', fading: false });
    setToaster(closedToaster);
  }, [notice, current]);

  const closeToaster = useCallback(() => {
    setToaster((prev) => ({ ...prev, open: false }));
  }, []);

  const followed = useMemo<FollowedCashin | null>(() => {
    const requestId = trackedId ?? verifiedId;
    if (!requestId) {
      return null;
    }
    const probe = trackingId ? tracking.probe : verification.probe;
    const item = items.find((entry) => entry.request_id === requestId);
    return {
      requestId,
      state: probe?.state ?? item?.state ?? 'in_progress',
      transfer: probe?.transfer ?? item?.transfer ?? null,
      cellphone: item?.cellphone ?? '',
      amount: item?.amount ?? '',
      accountOwner: item?.account_owner ?? '',
      ciSender: item?.ci_sender ?? '',
    };
  }, [trackedId, verifiedId, trackingId, tracking.probe, verification.probe, items]);

  return { notice, dismiss, banner, toaster, closeToaster, followed };
};
